import { useCallback, useEffect, useReducer, useRef } from "react";

import { sentryService } from "../sentry";
import type {
  FlowState,
  Needs,
  OnDone,
  Outcomes,
  ScreenKey,
  Tagged,
  TaggedUnion,
  TransitionTable,
} from "./";

type RuntimeAction =
  | { tag: string; data: Record<string, unknown> | undefined }
  | { type: "back" };

function isBackAction(action: RuntimeAction): action is { type: "back" } {
  return "type" in action && action.type === "back";
}

function buildState<TConfig>(
  tag: string,
  current: FlowState<TConfig>,
  actionData: Record<string, unknown>,
): FlowState<TConfig> {
  const data = current.data ?? {};
  const newData: Record<string, unknown> = {
    ...data,
    ...actionData,
  };
  // TS can't correlate a string `tag` with a merged record to a specific
  // FlowState variant; type safety is enforced upstream by FlowResult<TConfig, K>.
  return {
    tag,
    data: newData,
  } as FlowState<TConfig>;
}

// Terminal screens (`outcomes: never`) are excluded because `TaggedUnion<never>`
// would otherwise leak `Tagged<string, never>` into the outcome union
// (`keyof never & string` resolves to `string`), breaking narrowing on `from.tag`.
export type FlowTransition<TConfig> = {
  [K in ScreenKey<TConfig>]: [Outcomes<TConfig, K>] extends [never]
    ? never
    : {
        from: Tagged<K, Needs<TConfig, K>>;
        outcome: TaggedUnion<Outcomes<TConfig, K>>;
        to: FlowState<TConfig>;
      };
}[ScreenKey<TConfig>];

type InternalState<TConfig> = {
  current: FlowState<TConfig>;
  history: FlowState<TConfig>[];
};

function reducer<TConfig>(
  state: InternalState<TConfig>,
  action: RuntimeAction,
): InternalState<TConfig> {
  if (isBackAction(action)) {
    const prev = state.history.at(-1);
    if (!prev) {
      return state;
    }
    return {
      current: prev,
      history: state.history.slice(0, -1),
    };
  }
  return {
    current: buildState(action.tag, state.current, action.data ?? {}),
    history: [...state.history, state.current],
  };
}

export function useNavigableFlow<TConfig, TCtx = void>(config: {
  transitions: TransitionTable<TConfig, TCtx>;
  initial: FlowState<TConfig>;
  ctx: TCtx;
  onTransition?: (event: FlowTransition<TConfig>) => void;
  /**
   * Fires with the committed state on first render and after every
   * transition — always a render cycle after `onTransition`, which runs
   * synchronously in the outcome handler. Observability only; it cannot
   * redirect.
   */
  onStateLoaded?: (state: FlowState<TConfig>) => void;
}) {
  const { ctx, onTransition, onStateLoaded } = config;

  const [state, dispatch] = useReducer(reducer, {
    current: config.initial,
    history: [],
  });

  // Latest-ref so an inline callback prop neither re-fires an unchanged state
  // nor goes stale — the effect below keys on the committed state alone.
  const onStateLoadedRef = useRef(onStateLoaded);
  onStateLoadedRef.current = onStateLoaded;
  const current = state.current;
  useEffect(() => {
    onStateLoadedRef.current?.(current);
  }, [current]);

  const currentTag = state.current.tag;

  const handleOutcome = useCallback(
    <TScreen extends ScreenKey<TConfig>>(
      tag: TScreen,
      outcome: TaggedUnion<Outcomes<TConfig, TScreen>>,
    ) => {
      const handler = config.transitions[tag][outcome.tag];
      // `state.current.tag === tag` is invariant whenever handleOutcome runs, so
      // the current state is the source screen's state. TS can't correlate the
      // union member to `tag`, hence the cast.
      const action = handler(
        outcome.data,
        ctx,
        state.current as Tagged<TScreen, Needs<TConfig, TScreen>>,
      );
      // Compute nextState eagerly so `onTransition` can carry the full resolved
      // FlowState; the reducer will recompute the same value.
      const nextState: FlowState<TConfig> = isBackAction(action)
        ? (state.history.at(-1) ?? state.current)
        : buildState(action.tag, state.current, action.data ?? {});
      // Correlated union: TS can't see that `state.current.tag === tag` is
      // invariant when handleOutcome is called, so it can't prove the object
      // lands in the matching variant of `FlowTransition<TConfig>`.
      onTransition?.({
        from: state.current,
        outcome,
        to: nextState,
      } as FlowTransition<TConfig>);
      dispatch(action);
    },
    [config.transitions, ctx, state, onTransition],
  );

  const hasBackHistory = state.history.length > 0;
  const stateHasBackOutcome = "back" in (config.transitions[currentTag] ?? {});

  const canGoBack = hasBackHistory && stateHasBackOutcome;

  const goBack = useCallback(() => {
    if (!canGoBack) {
      // Warn instead of silently no-op'ing so a miswired caller is visible in dev.
      sentryService.captureMessage(
        "useNavigableFlow: goBack() called on screen but canGoBack is false",
        {
          level: "warning",
          extra: {
            currentTag,
            hasBackHistory,
            stateHasBackOutcome,
          },
        },
      );
      return;
    }
    handleOutcome(
      currentTag,
      // Route through the user's `back` handler rather than bypassing it.
      // `canGoBack` already guarantees the handler exists; the cast is needed
      // because `"back"` isn't universally in `keyof Outcomes<…>`.
      { tag: "back", data: {} } as TaggedUnion<
        Outcomes<TConfig, typeof currentTag>
      >,
    );
  }, [
    hasBackHistory,
    stateHasBackOutcome,
    canGoBack,
    handleOutcome,
    currentTag,
  ]);

  const bindOnDone = <TScreen extends ScreenKey<TConfig>>(
    tag: TScreen,
  ): OnDone<Outcomes<TConfig, TScreen>> => {
    return outcome => handleOutcome(tag, outcome);
  };

  return {
    state: state.current,
    // The full navigation stack, oldest first, with `current` on top. A
    // renderer can keep every visited screen mounted (top visible, the rest
    // hidden) so a screen's component state survives navigating away and back.
    stack: [...state.history, state.current],
    bindOnDone,
    canGoBack,
    goBack,
  };
}
